import {
  Briefcase,
  Circle,
  Layers,
  Loader,
  PackagePlus,
  Plus,
  Search,
  Sigma,
  SortAsc,
} from "lucide-react";
import { useState } from "react";
import LoanCard from "../../shared/LoanCard";
import { useNavigate } from "react-router-dom";
import { LoanProductsSkeleton } from "../../skeletons/LoanProductsSkeleton";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useQuery } from "react-query";
import {
  getLoanProducts,
  getProductStatistics,
} from "../../sdk/products/products";
import ProductFilters from "../../components/filter/ProductFilters";
import Pagination from "../../shared/Pagination";
import Loading from "../../shared/Loading";
import { hasPermission } from "../../utils/permissions";
import useAuth from "../../hooks/useAuth";

const LoanProducts = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const formattedDate = useFormattedDate();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [loanProducts, setLoanProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [interestType, setInterestType] = useState("");
  const [productType, setProductType] = useState("");
  const [repaymentFrequency, setRepaymentFrequency] = useState("");

  const {
    refetch: refetchProducts,
    isRefetching,
    isFetching,
  } = useQuery({
    queryKey: [
      "loan-products",
      page,
      limit,
      productType,
      interestType,
      repaymentFrequency,
    ],
    queryFn: async () => {
      const response = await getLoanProducts(
        page,
        limit,
        productType,
        interestType,
        repaymentFrequency,
        query,
      );
      return response;
    },
    onSuccess: (data) => {
      setLoanProducts(data?.data);
      setPage(data?.meta?.page);
      setLimit(data?.meta?.limit);
      setTotalPages(data?.meta?.totalPages);
      setTotalItems(data?.meta?.total);
    },
  });

  const allProductIds = loanProducts.map((p) => p.id);
  const isAllSelected =
    allProductIds.length > 0 &&
    selectedProducts.length === allProductIds.length;

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(allProductIds);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const { data: productsStatistics } = useQuery({
    queryKey: ["products statistics"],
    queryFn: () => getProductStatistics(),
  });

  const toggleProductFilter = () => {
    setOpenFilter(true);
  };

  const closeProductFilter = () => {
    setOpenFilter(false);
  };

  const handleApplyFilters = () => {
    setOpenFilter(false);
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  return (
    <>
      {openFilter && (
        <ProductFilters
          closeFilters={closeProductFilter}
          handleApplyFilter={handleApplyFilters}
          status={status}
          setStatus={setStatus}
          selectedInterestType={interestType}
          setSelectedInterestType={setInterestType}
          selectedProductType={productType}
          setSelectedProductType={setProductType}
          repaymentFrequency={repaymentFrequency}
          setRepaymentFrequency={setRepaymentFrequency}
        />
      )}
      {isFetching ? (
        <LoanProductsSkeleton />
      ) : (
        <div className="pl-[10px] w-full">
          <div className="flex my-[10px] items-center justify-between">
            <p className="text-[28px] font-bold text-[#222222]">
              Loan Products
            </p>
            {hasPermission(
              auth?.roles,
              "ea1ddfd4-d879-cd8d-c502-006893e8afc4",
            ) && (
              <button
                onClick={() => navigate("/dashboard/loan-products/create")}
                className="bg-[#000000] h-[40px] rounded-[30px] text-[#ffffff] text-[14px] font-normal px-[20px] flex items-center justify-center gap-[5px]"
              >
                <Plus size={16} color="#ffffff" />
                Create a New Product
              </button>
            )}
          </div>
          <div className="flex items-center mb-4 gap-[20px]">
            <LoanCard
              icon={<Sigma />}
              label="Total number of products"
              value={productsStatistics?.totalNumberOfProducts ?? 0}
              padColor="#4B5D67"
            />
            <LoanCard
              icon={<Circle color="#00A676" />}
              label="Active products"
              value={productsStatistics?.activeProducts ?? 0}
              padColor="#00A676"
            />
            <LoanCard
              icon={<Circle color="#000000" />}
              label="Inactive/Deactivated"
              value={productsStatistics?.inactiveProducts ?? 0}
              padColor="#000000"
            />
          </div>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <button
              onClick={refetchProducts}
              className="h-[40px] w-[117px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]"
            >
              {isRefetching ? <Loading /> : <Loader size={17} />}
              Update
            </button>
            <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
              {selectedProducts?.length} Selected
            </div>
            <div
              onClick={toggleProductFilter}
              className="h-[40px] cursor-pointer w-[127px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
            >
              <SortAsc color="#000000" />
              <span className="text-[#000000]">Filter</span>
              <span className="text-[#000000]">0</span>
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-2 h-[40px] w-[50%] bg-white">
              <Search className="text-gray-500 w-5 h-5 mr-2" />
              <input
                type="text"
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products"
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
              />
              <button
                onClick={refetchProducts}
                className="ml-2 p-2 bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Search className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[#000000] text-[15px] font-normal">
              {loanProducts?.length} Results
            </p>
          </div>
          <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
            <div className="w-[3%]">
              <input
                type="checkbox"
                name="loan"
                id="loan"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </div>
            <div className="w-[7%] text-[#000] font-bold text-[13px] truncate">
              PROD ID
            </div>
            <div className="w-[18%] text-[#000] font-bold text-[13px] truncate">
              PROD NAME
            </div>
            <div className="w-[14%] text-[#000] font-bold text-[13px] truncate">
              TYPE
            </div>
            <div className="w-[8%] text-[#000] font-bold text-[13px] truncate">
              REPAYMENT FREQUENCY
            </div>
            <div className="w-[10%] truncate text-[#000] font-bold text-[13px] truncate">
              INTEREST RATE
            </div>
            <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
              LOAN TYPE
            </div>
            <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
              CREATED ON
            </div>
            <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
              UPDATED AT
            </div>
            <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
              STATUS
            </div>
          </div>
          {loanProducts?.length > 0 ? (
            loanProducts?.map((product, index) => (
              <div
                key={product?.id}
                className="h-[40px] cursor-pointer px-[10px] w-full bg-[#ffffff] mb-[10px] flex items-center"
              >
                <div className="w-[3%]">
                  <input
                    type="checkbox"
                    name="loan"
                    id={`loan-${index}`}
                    checked={selectedProducts.includes(product?.id)}
                    onChange={() => handleSelectOne(product?.id)}
                  />
                </div>
                <div
                  onClick={() =>
                    navigate(`/dashboard/loan-products/${product.id}`)
                  }
                  className="w-[7%] cursor-pointer text-[#2A8C52] font-normal text-[14px]"
                >
                  {product.public_id}
                </div>
                <div className="w-[18%] text-[#000] truncate font-normal text-[14px]">
                  {product.name}
                </div>
                <div className="w-[14%] text-[#000] font-normal text-[14px]">
                  {product?.loanType ?? "N/A"}
                </div>
                <div className="w-[8%] text-[#000] font-normal text-[14px] capitalize">
                  {product?.repaymentFrequency ?? "N/A"}
                </div>
                <div className="w-[10%] text-[#000] font-normal text-[14px]">
                  {product.defaultInterestRate}%
                </div>
                <div className="w-[10%] text-[#000] capitalize font-normal text-[14px]">
                  {product.repaymentType}
                </div>
                <div className="w-[10%] text-[#000] font-normal text-[14px]">
                  {formattedDate(product.createdAt)}
                </div>
                <div className="w-[10%] text-[#000] font-normal text-[14px]">
                  {formattedDate(product.updatedAt)}
                </div>
                <div className="w-[10%] capitalize text-[#000] font-normal text-[14px]">
                  {product.status}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-28 px-6 bg-white border-2 border-gray-100 relative overflow-hidden">
              {/* Decorative Background Pattern */}
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] pointer-events-none">
                <Layers size={400} className="text-[#2A8C52]" />
              </div>

              {/* Icon Container with ZipGo Green glow */}
              <div className="relative mb-8 z-10">
                <div className="absolute inset-0 bg-[#2A8C52] blur-[35px] opacity-15 rounded-full scale-150" />

                <div className="relative w-24 h-24 bg-white rounded-[28px] flex items-center justify-center border border-gray-100 shadow-2xl shadow-gray-200/40">
                  <Briefcase size={42} className="text-[#2A8C52] opacity-90" />

                  {/* Action Badge */}
                  <div className="absolute -top-1 -right-1 w-9 h-9 bg-[#2A8C52] rounded-xl flex items-center justify-center border-4 border-white shadow-lg">
                    <Plus size={18} className="text-white" strokeWidth={3} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="max-w-md text-center relative z-10">
                <h3 className="text-gray-900 font-extrabold text-2xl tracking-tight">
                  No Loan Products Defined
                </h3>
                <p className="text-gray-500 mt-3 text-[15px] leading-relaxed">
                  You haven't created any loan products yet. Define your
                  interest rates, durations, and repayment terms to start
                  offering credit to your borrowers.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto relative z-10">
                <button
                  onClick={() => navigate("/dashboard/loan-products/create")}
                  className="sm:w-auto px-8 py-3.5 bg-[#2A8C52] text-white text-sm font-bold rounded-2xl hover:bg-[#227042] hover:shadow-xl hover:shadow-green-900/20 transition-all flex items-center justify-center gap-2 group"
                >
                  <PackagePlus
                    size={18}
                    className="group-hover:scale-110 transition-transform"
                  />
                  Create New Product
                </button>

                <button className="sm:w-auto px-8 py-3.5 bg-white text-gray-600 text-sm font-bold rounded-2xl border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                  <Search size={18} />
                  Search Catalog
                </button>
              </div>
            </div>
          )}
          <div className="h-[50px] mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {start}–{end} of {totalItems}
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                Row/Page:
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {page}/{totalPages}
              </p>
              <select
                className="h-[35px] w-[100px] px-[10px] text-[12px]"
                name="row"
                id="row"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="25">25 items</option>
              </select>
            </div>
            <div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanProducts;
